import { injectable, inject, injectFromBase } from 'inversify'
import BaseFilters from '@/services/tables/filters/baseFilters'
import type { FiltersToCreate } from '@/services/tables/filters/types'
import type { SelectStatusOption } from '@/services/forms/types'
import { SERVICE_TYPES } from '@/core/container/types'
import type FilterSchemaService from '@/services/filterSchemaService'
import type LoggerService from '@/services/loggerService'
import type { FilterBuilderService } from '@/services/tables/filters/filterBuilderService'
import type TranslateService from '@/services/translateService'
import type { FilterStrategyManager } from '@/services/tables/filters/strategies/filterStrategyManager'
import type UserService from '@/services/domain/user/userService'
import { UserFilterName, UserStatus } from '@/services/domain/user/types'

@injectable()
@injectFromBase()
export default class UserFilterService extends BaseFilters {
  constructor(
    @inject(SERVICE_TYPES.FilterSchemaService) protected readonly filterSchemaService: FilterSchemaService,
    @inject(SERVICE_TYPES.LoggerService) protected readonly loggerService: LoggerService,
    @inject(SERVICE_TYPES.FilterStrategyManager) protected readonly filterStrategyManager: FilterStrategyManager,
    @inject(SERVICE_TYPES.FilterBuilderService) protected readonly filterBuilderService: FilterBuilderService,
    @inject(SERVICE_TYPES.TranslateService) protected readonly translateService: TranslateService,
    @inject(SERVICE_TYPES.UserService) protected readonly userService: UserService,
  ) {
    super(filterSchemaService, loggerService, filterStrategyManager, filterBuilderService, translateService)
  }

  private get statusesPropsOptions(): SelectStatusOption[] {
    return [
      {
        text: this.translateService.t('status.user.active'),
        value: this.userService.getStatusesForEnabledSubAccount().join(','),
        color: 'green',
        appearance: 'pastel',
        outline: true,
      },
      {
        text: this.translateService.t('status.user.closed'),
        value: this.userService.getStatusesForClosedSubAccount().join(','),
        color: 'gray',
        appearance: 'pastel',
        outline: true,
      },
      {
        text: this.translateService.t('status.user.subInvited'),
        value: UserStatus.ST_SUB_INVITED.toString(),
        color: 'orange',
        appearance: 'intense',
        outline: true,
      },
    ]
  }

  protected getFilters(): FiltersToCreate {
    return [
      {
        name: UserFilterName.STATUS,
        icon: 'filter_alt',
        label: this.translateService.t('common.status'),
        operation: 'in',
        relatedFieldName: '',
        isDefault: true,
        callback: (data) => {
          data.options = this.statusesPropsOptions.map((opts) => ({
            value: opts.value,
            text: opts.text,
            color: opts.color,
            appearance: opts.appearance,
            outline: opts.outline,
          }))
          return data
        },
      },
    ]
  }
}
